import { ApplicationStatus } from "../../services/eventOpportunitiesApi";

export type TrackerChoice = {
  status: ApplicationStatus;
  label: string;
  hint: string;
};

// The plain-language choices a vendor sees when tracking an application. The
// server accepts any of these from any starting point.
export const TRACKER_CHOICES: TrackerChoice[] = [
  {
    status: "planning-to-apply",
    label: "Not applied yet",
    hint: "I plan to apply",
  },
  { status: "applied", label: "Applied", hint: "Waiting to hear back" },
  {
    status: "waitlisted",
    label: "Waitlisted",
    hint: "They put me on a waiting list",
  },
  {
    status: "rejected",
    label: "Didn't get in",
    hint: "My application was declined",
  },
  {
    status: "payment-due",
    label: "Accepted, payment due",
    hint: "I'm in, but haven't paid the booth fee yet",
  },
  {
    status: "confirmed",
    label: "Accepted and paid",
    hint: "I'm in and my spot is confirmed",
  },
  {
    status: "skipped",
    label: "Skipping this event",
    hint: "I'm not going to apply",
  },
];

const EXTRA_LABELS: Partial<Record<ApplicationStatus, string>> = {
  interested: "Interested",
  saved: "Saved",
  researching: "Researching",
  accepted: "Accepted",
  withdrawn: "Withdrawn",
  attended: "Attended",
  cancelled: "Cancelled",
};

export const trackerStatusLabel = (status?: ApplicationStatus): string =>
  (status &&
    (TRACKER_CHOICES.find((choice) => choice.status === status)?.label ||
      EXTRA_LABELS[status])) ||
  "Track application";

// Applications happen on the organizer's own channels, so we send the vendor
// to a Google search for them.
export const buildGoogleApplySearchUrl = (opportunity: {
  name: string;
  city?: string;
  state?: string;
}): string => {
  const terms = [
    opportunity.name,
    opportunity.city,
    opportunity.state,
    "vendor application",
  ]
    .filter(Boolean)
    .join(" ");
  return `https://www.google.com/search?q=${encodeURIComponent(terms)}`;
};

export type ExternalApplyLink = {
  url: string;
  /** "organizer" = a link the listing gave us; "search" = our Google fallback. */
  kind: "organizer" | "search";
};

const httpUrl = (value?: string | null): string | null => {
  if (!value) return null;
  try {
    const url = new URL(value.trim());
    return url.protocol === "http:" || url.protocol === "https:"
      ? url.toString()
      : null;
  } catch {
    return null;
  }
};

// Every listing gets a way out of the app to apply: the organizer's own
// application or website when we have one, otherwise a Google search. Only
// http(s) links are used, since listings can come from outside sources.
export const externalApplyLink = (opportunity: {
  name: string;
  city?: string;
  state?: string;
  applicationUrl?: string;
  officialWebsite?: string;
}): ExternalApplyLink => {
  const direct =
    httpUrl(opportunity.applicationUrl) || httpUrl(opportunity.officialWebsite);
  return direct
    ? { url: direct, kind: "organizer" }
    : { url: buildGoogleApplySearchUrl(opportunity), kind: "search" };
};

type DateFields = {
  datesConfirmed?: boolean;
  status?: string;
  requirements?: Record<string, unknown>;
};

// The event ended or has no known dates; it is listed because most return yearly.
export const hasUnconfirmedDates = (opportunity: DateFields): boolean =>
  opportunity.datesConfirmed === false;

export const unconfirmedDatesLabel = (opportunity: DateFields): string =>
  opportunity.status === "passed"
    ? "Ended · next dates not confirmed"
    : "Dates not confirmed";

export const UNCONFIRMED_DATES_NOTE =
  "This event has ended or its next dates aren't confirmed yet. Many events return every year, so check with the organizer using the link below before you plan on it.";

// The listing's own schedule wording, e.g. "Saturdays, 9 AM - 1 PM".
export const scheduleText = (opportunity: DateFields): string | undefined => {
  const text = (
    opportunity.requirements?.schedule as { text?: string } | undefined
  )?.text;
  return typeof text === "string" && text.trim() ? text.trim() : undefined;
};

// Tapping the choice that is already selected clears it (back to a plain
// "saved" event with nothing highlighted), so a mis-tap can be undone.
export const toggleTrackerStatus = (
  current: ApplicationStatus | undefined,
  chosen: ApplicationStatus,
): ApplicationStatus => (current === chosen ? "saved" : chosen);

export type EventLook = { label: string; color: string };

// Order matters: "Craft Fair" must be a craft fair, not a festival.
const LOOKS: Array<{ match: RegExp } & EventLook> = [
  { match: /farmer/i, label: "Farmers market", color: "#2F9E44" },
  {
    match: /\b(craft|art|maker|handmade)/i,
    label: "Craft fair",
    color: "#7048E8",
  },
  { match: /festival|\bfair\b/i, label: "Festival", color: "#E8590C" },
  { match: /pop-?up|market/i, label: "Pop-up market", color: "#1C7ED6" },
];

// Most imported events have no photo, so a card without one gets a colored
// bar and label for the kind of event instead of an empty gray box.
export const eventLook = (categories: string[] | undefined): EventLook => {
  for (const category of categories || []) {
    const found = LOOKS.find((look) => look.match.test(category));
    if (found) return { label: found.label, color: found.color };
  }
  return { label: "Vendor event", color: "#868E96" };
};
